<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {usePiniaStore} from "../../store/pinia_store";
import Vue from "vue";
import _, {isEmpty} from "lodash";
import VoucherLines from "./VoucherLines.vue";
import moment from "moment";

const props = defineProps({
  dentaidApiInstance: {
    type: Function,
    required: true,
  },
  eventBus: {
    type: Object,
    required: true,
  },
  deliveryDate: {
    type: String,
    required: false,
  },
  comment: {
    type: String,
    required: false,
  },
  reference: {
    type: String,
    required: false,
  },
  wholesaler: {
    type: String | Number,
    required: false,
  },
  canOrder: {
    type: Boolean,
    required: true,
  }
})

const piniaStore = usePiniaStore();

const shoppingCartTitle = window.Drupal.t('Shopping cart');
const loadingText = window.Drupal.t('Loading...');
const freeShippingText = window.Drupal.t('Free shipping from € 100, - excl VAT.');
const shippingCostText = window.Drupal.t('Shipping cost');
const totalText = window.Drupal.t('Subtotal (excl VAT.)');
const discountText = window.Drupal.t('Discount');
const subTotalText = window.Drupal.t('Subtotal');
const productsText = window.Drupal.t('Products');
const orderOverviewText = window.Drupal.t('Order overview');
const placeOrderText = window.Drupal.t('Place order');
const orderErrorText = window.Drupal.t('Something went wrong while placing your order');
const chosenGiftText = window.Drupal.t('Chosen gift:');
const hasCartLines = ref(isEmpty(piniaStore.cart.lines))
const isLoading = ref(false);
const selectedGift = ref(null);

const language = computed(() => {
  let lang = window.location.pathname.split('/')[1];

  if (lodash.isNil(lang)) {
    lang = 'nl';
  }

  return lang;
});

const getProducts = computed(() => {
  return Object.assign({}, _.filter(piniaStore.cart.lines, function (cartLine) {
    return cartLine.type === 'Product';
  }));
});

const getVouchers = computed(() => {
  return Object.assign({}, _.filter(piniaStore.cart.lines, function (cartLine) {
    return cartLine.type === 'Voucher';
  }));
});

const getGifts = computed(() => {
  return Object.assign({}, _.filter(piniaStore.cart.lines, function (cartLine) {
    return cartLine.type === 'Gift';
  }));
});

const handleOrder = () => {
  isLoading.value = true;

  let deliveryDate = null;
  try {
    deliveryDate = moment(new Date(props.deliveryDate.split('-').reverse().join('-')).toISOString()).format('YYYY-MM-DD');
  } catch (e) {
    deliveryDate = moment().format('YYYY-MM-DD');
  }

  props.dentaidApiInstance.post(`/cart/checkout?token=${piniaStore.parsedToken}`, {
    delivery_at: deliveryDate,
    comment: props.comment,
    reference: props.reference,
    wholesaler_id: props.wholesaler,
  })
    .then(response => {
      storePurchaseDataForThankYouPage(response.data);

      window.location = `/checkout/order/${response.data}/thank-you`;
    })
    .catch(error => {
      isLoading.value = false;

      console.log(error);

      Vue.$toast.error(orderErrorText);
    })
}

const currencyFormatter = (value) => {
  return new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(value / 100);
}

const formatPriceForDataLayer = (priceInCents) => {
  return (priceInCents / 100).toFixed(2);
}

const calculateDiscountPercentage = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;

  if (totalUnitPrice === 0) {
    return 0;
  }

  const discountPercentage = ((totalUnitPrice - totalNetPrice) / totalUnitPrice) * 100;
  return parseFloat(discountPercentage.toFixed(4));
}

const calculateUnitDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const quantity = product.quantity || 1;
  const discountPercentage = calculateDiscountPercentage(product);
  const discountPerUnit = (totalUnitPrice - totalNetPrice) / quantity;
  return formatPriceForDataLayer(discountPerUnit);
}

const calculateTotalProductDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const totalDiscount = totalUnitPrice - totalNetPrice;
  return formatPriceForDataLayer(totalDiscount);
}

const getAppliedVoucherCodes = () => {
  const voucherLines = _.filter(piniaStore.cart.lines, (line) => line.type === 'Voucher');
  const voucherCodes = _.map(voucherLines, (voucher) => voucher.code || voucher.name);
  return voucherCodes.join(', ');
}

const getProductLines = () => {
  return _.filter(piniaStore.cart.lines, (line) => line.type === 'Product');
}

const storePurchaseDataForThankYouPage = (transactionId) => {
  const productLines = getProductLines();
  const appliedCoupons = getAppliedVoucherCodes();

  const items = _.map(productLines, (product, index) => {
    const brand = product.translation?.extra_attributes?.brand || '';
    const totalProductPrice = formatPriceForDataLayer(product.unit_price * (product.quantity || 1));
    const totalProductDiscount = calculateTotalProductDiscount(product);
    return {
      item_id: product.sku || '',
      item_name: product.dwh_product?.name?.toLowerCase() || product.name?.toLowerCase() || '',
      affiliation: brand,
      coupon: appliedCoupons,
      discount: calculateUnitDiscount(product),
      index: index,
      item_brand: brand,
      item_category: product.translation?.extra_attributes?.category || '',
      ...(piniaStore.cart.id && { item_list_id: String(piniaStore.cart.id) }),
      item_variant: product.translation?.extra_attributes?.volume || '',
      price: formatPriceForDataLayer(product.unit_price),
      quantity: product.quantity || 1,
      total_product_price: (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
      total_product_discount: totalProductDiscount,
    }
  });

  const purchaseData = {
    transaction_id: String('T-' + transactionId),
    value: formatPriceForDataLayer(piniaStore.cart.total_net_price),
    tax: formatPriceForDataLayer(0),
    shipping: formatPriceForDataLayer(piniaStore.cart.shipping_costs),
    discount: formatPriceForDataLayer(piniaStore.cart.discount),
    ...(appliedCoupons && { coupon: appliedCoupons }),
    items: items,
  };

  sessionStorage.setItem('dentaid_purchase_data', JSON.stringify(purchaseData));
}


const pushCheckOrderToDataLayer = () => {
  const productLines = getProductLines();

  if (isEmpty(productLines)) {
    return;
  }

  const appliedCoupons = getAppliedVoucherCodes();

  const items = _.map(productLines, (product, index) => {
    const brand = product.translation?.extra_attributes?.brand || '';
    const totalProductPrice = formatPriceForDataLayer(product.unit_price * (product.quantity || 1));
    const totalProductDiscount = calculateTotalProductDiscount(product);
    return {
      item_id: product.sku || '',
      item_name: (product.dwh_product?.name ?? product.translation?.name)?.toLowerCase() || '',
      affiliation: brand,
      coupon: appliedCoupons,
      discount: calculateUnitDiscount(product),
      index: index,
      item_brand: brand,
      item_category: product.translation?.extra_attributes?.category || '',
      item_list_id: String(piniaStore.cart.id || ''),
      item_variant: product.translation?.extra_attributes?.volume || '',
      price: formatPriceForDataLayer(product.unit_price),
      quantity: product.quantity || 1,
      total_product_price: (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
      total_product_discount: totalProductDiscount,
    }
  });

  window.dataLayer.push({ecommerce: null});

  window.dataLayer.push({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'EUR',
      value: formatPriceForDataLayer(piniaStore.cart.total),
      discount: formatPriceForDataLayer(piniaStore.cart.discount),
      ...(appliedCoupons && { coupon: appliedCoupons }),
      items: items,
    }
  });
}

const checkInitialGift = () => {
  let gift = _.find(piniaStore.cart.lines, (cartLine) => {
    return cartLine.type === 'Gift';
  });

  if (lodash.isNil(gift)) {
    return;
  }

  selectedGift.value = _.find(piniaStore.gifts.lines, (giftLine) => {
    return giftLine.sku === gift.sku
  });
}

watch(() => piniaStore.isLoading, (isLoading) => {
  if (!isEmpty(getGifts)) {
    checkInitialGift();
  }

  // Push begin_checkout event when cart data is loaded
  if (!isLoading && !isEmpty(getProductLines())) {
    pushCheckOrderToDataLayer();
  }
}, {deep: true})

onMounted(() => {
  props.eventBus.$on('get-cart-completed', () => pushCheckOrderToDataLayer());
})
</script>

<template>
  <div class="vue-checkout-mini-cart">
    <template v-if="piniaStore.isLoading">
      <h3>{{ loadingText }}
        <dentaid-loader></dentaid-loader>
      </h3>
    </template>

    <template v-if="hasCartLines & !piniaStore.isLoading">
      <div class="totals-wrapper">
        <h3>{{ orderOverviewText }}</h3>

        <div class="products">
          {{ productsText }} <span
          class="price">{{ currencyFormatter(piniaStore.cart.total_unit_price) }}</span>
        </div>

        <VoucherLines
          v-if="!isEmpty(getVouchers)"
          :voucher-lines="getVouchers"
        ></VoucherLines>

        <div class="discount mt-3" v-if="!isEmpty(getGifts)">
          {{ chosenGiftText }} {{ selectedGift.name }}
        </div>

        <div class="discount" v-if="piniaStore.cart.discount">
          {{ discountText }} <span class="price">- {{ currencyFormatter(piniaStore.cart.discount) }}</span>
        </div>

        <div class="subtotal">
          {{ subTotalText }} <span class="price">{{
            currencyFormatter(piniaStore.cart.total_net_price)
          }}</span>
        </div>

        <div class="shipping-cost">
          {{ shippingCostText }} <span class="price">{{
            currencyFormatter(piniaStore.cart.shipping_costs)
          }}</span>
        </div>

        <div class="shipping-info">
          {{ freeShippingText }}
        </div>

        <div class="total">
          {{ totalText }} <span class="price">{{ currencyFormatter(piniaStore.cart.total) }}</span>
        </div>

      </div>

      <button class="btn-primary" :disabled="!canOrder" @click="handleOrder">
        {{ placeOrderText }}
        <template v-if="isLoading">
          <dentaid-loader></dentaid-loader>
        </template>
      </button>
    </template>
  </div>
</template>
